<script setup>
// One block of the member booking grid, and the legend's swatch (X47, X54).
// The legend renders THIS component with the same class as the grid does, so
// a legend entry always looks exactly like the cells it explains (Grant
// 2026-10-19: "they MUST match the cells"). States and their classes come
// from utils/bookingWeek CELL_STATES: free (the price or "Free", bookable by
// this viewer right now; never a "+", ruling 8), taken, mine ("You"), open
// (dotted, walk in), cant (hatched + an icon: closed, frozen, matches only,
// not yet open, at limit, no access, card payments off); X91: anything ended is faded by one white veil (.bwc--past).
// X54 (ruling 7): flat cells with no radius or border of their own; the grid
// draws the hairlines. A booking block (ruling 6) shows its word and its
// time range on two lines when it is tall enough, else the one-line form.
// X57: the admin calendar draws its blocks with this component too, so the
// two grids and both legends share one set of state styles: a default slot
// carries the admin's own words (who booked, the note, chips) in place of the
// text spans, and three admin-only states join the list: match (a competition
// match's booking), meet (held for a meet) and matchesonly (time kept for
// matches, which an admin may still book with a chip).
defineProps({
  /** A block from columnBlocks ({cls, icon, text, sub, line, mini, past, bookable}) or a cell model; a swatch needs only cls and icon. */
  model: {type: Object, required: true},
  /** The legend's small rectangle (no text, not a button). */
  swatch: {type: Boolean, default: false},
  selected: {type: Boolean, default: false},
  /** Two lines (word + time range) fit: a booking block two or more rows tall. */
  tall: {type: Boolean, default: false},
  /** X243 (TEST_FT-R3 M-B): the words sit in the block's first row, not down its middle (the scheduling table's tall This match). */
  top: {type: Boolean, default: false},
});
</script>

<template>
  <i v-if="swatch" class="bwc bwc--swatch" :class="'bwc--' + model.cls" aria-hidden="true">
    <v-icon v-if="model.icon" :icon="model.icon" size="12"/>
  </i>
  <span v-else class="bwc" :class="['bwc--' + model.cls, {'bwc--past': model.past, 'bwc--selected': selected, 'bwc--tap': model.bookable, 'bwc--tall': tall, 'bwc--top': top}]">
    <slot v-if="$slots.default"/>
    <span v-else class="bwc-in">
      <v-icon v-if="model.icon" :icon="model.icon" size="13" class="bwc-icon"/>
      <template v-if="tall">
        <span class="bwc-text bwc-word">{{ model.text }}</span>
        <span v-if="model.sub" class="bwc-text bwc-sub">{{ model.sub }}</span>
      </template>
      <template v-else>
        <span class="bwc-text bwc-full">{{ model.line ?? model.text }}</span>
        <span class="bwc-text bwc-mini">{{ model.mini ?? model.text }}</span>
      </template>
    </span>
  </span>
</template>

<style scoped lang="scss">
/* One rule per state, used by the grid's blocks and the legend's swatches alike. Flat: the grid draws the lines. */
.bwc {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 4px;
  width: 100%;
  height: 100%;
  padding: 0 6px;
  border: 0;
  border-radius: 0;
  background: rgb(var(--v-theme-surface));
  color: rgb(var(--v-theme-charcoal));
  font-size: 11.5px;
  font-weight: 600;
  line-height: 1.15;
  overflow: hidden;
  transition: background .12s ease, box-shadow .12s ease, color .12s ease;
}
/* The words inside a block (the icon and the text, in a row; a column on a tall block). */
.bwc-in { display: flex; align-items: center; justify-content: center; gap: 4px; max-width: 100%; min-width: 0; }
.bwc--tall > .bwc-in { flex-direction: column; gap: 1px; }
/* X243: a tall block's words in its first row (the grid's row height, less the booking block's 3px inset). */
.bwc--top { align-items: flex-start; }
.bwc--top > .bwc-in { height: calc(var(--bw-rh-eff, 34px) - 3px); }
.bwc-text { white-space: nowrap; overflow: hidden; text-overflow: ellipsis; max-width: 100%; }
.bwc-word { font-weight: 700; }
.bwc-sub { font-size: 10.5px; font-weight: 600; opacity: .85; }
.bwc-icon { flex: 0 0 auto; }
.bwc-mini { display: none; }

/* Free: the price alone, quiet (most cells are free); the brand comes on hover, focus and when picked. */
.bwc--free {
  color: rgb(var(--v-theme-text));
  background: rgb(var(--v-theme-surface));
  font-weight: 600;
}
.bwc--tap:hover, .bwc--tap:focus-visible, .bw-block:focus-visible > .bwc--tap, .bw-block:hover > .bwc--tap {
  background: rgb(var(--v-theme-brandTint));
  color: rgb(var(--v-theme-primary));
  box-shadow: inset 0 0 0 1px rgb(var(--v-theme-primary));
}
.bwc--selected { background: rgb(var(--v-theme-primary)) !important; color: #fff !important; box-shadow: none !important; }

/* Taken: solid neutral, the booking's word and its time range. X233 (R97 O-2): its ink is a step darker than the kit's
   charcoal (#6B6B6B on this grey was 4.1:1) so its small words pass 4.5:1 (#5C5C5C, 5.2:1), the time line at full
   strength too; the light grey fill is unchanged. Shared by /book and the scheduling table (the admin calendar's
   bookings are .bwc--mine, untouched). */
.bwc--taken {
  background: #E2E2E2;
  color: #5C5C5C;
  font-weight: 700;
  .bwc-sub { opacity: 1; }
}

/* Yours: the brand tint with a brand edge. */
.bwc--mine {
  background: rgb(var(--v-theme-brandTint));
  color: rgb(var(--v-theme-primary));
  font-weight: 700;
  box-shadow: inset 0 0 0 1px rgb(var(--v-theme-primary));
}

/* X57, the admin calendar: a match's booking (green), a meet's hold (amber), and time kept for matches (a dashed
   ring on white: an admin may still book it, with a chip). */
.bwc--match {
  background: rgb(var(--v-theme-successTint));
  color: rgb(var(--v-theme-success));
  font-weight: 700;
  box-shadow: inset 0 0 0 1px rgb(var(--v-theme-success));
}
.bwc--meet {
  background: rgb(var(--v-theme-warningTint));
  color: rgb(var(--v-theme-warning));
  font-weight: 700;
  box-shadow: inset 0 0 0 1px rgb(var(--v-theme-warning));
}
.bwc--matchesonly {
  background: rgb(var(--v-theme-surface));
  color: rgb(var(--v-theme-charcoal));
  outline: 1px dashed rgb(var(--v-theme-charcoal));
  outline-offset: -2px;
}

/* X72, the admin calendar: a change of this day's hours (the band in the change lane and the legend's swatch): amber,
   the kind's icon inside; the kind itself is on the stripe's label. */
.bwc--changed {
  background: rgb(var(--v-theme-warningTint));
  color: rgb(var(--v-theme-warning));
  font-weight: 700;
  box-shadow: inset 0 0 0 2px rgb(var(--v-theme-warning));
}

/* Open play: a light dotted fill, walk in. X58 (R23a L6): the band's words sit on a solid pill so the dots never run
   through the letters; the fill (and so the legend's swatch) is unchanged. */
.bwc--open {
  background-color: rgb(var(--v-theme-surface));
  background-image: radial-gradient(rgba(107, 107, 107, .3) 1px, transparent 1.3px);
  background-size: 7px 7px;
  color: rgb(var(--v-theme-textSubtle));
  > .bwc-in { padding: 2px 8px; border-radius: 999px; background: rgb(var(--v-theme-surface)); box-shadow: 0 0 0 1px rgb(var(--v-theme-borderLight)); }
}

/* Can't book: hatched, with the reason's icon. */
.bwc--cant {
  background: repeating-linear-gradient(135deg, #F8F8F8 0 6px, #E9E9E9 6px 8px);
  color: rgb(var(--v-theme-charcoal));
}

/* X231 (F-FIX3 decision 1, the app's CellLook.tooShort): the scheduling table's start the match would run past. Never
   the white of a free cell, and not hatched like a closed or frozen one. X232 (R96 L4): the kit's amber tint with its
   amber ink (warningInk on warningTint, 4.9:1), so it reads "free, but not long enough" beside the grey of Taken
   (the old #9B9B9B on #F1F1F1 was 2.4:1 and looked like Taken). The legend's swatch is this same cell. The word may
   take two lines in a narrow column. */
.bwc--short {
  background: rgb(var(--v-theme-warningTint));
  color: rgb(var(--v-theme-warningInk));
  font-size: 10.5px;
  .bwc-text { white-space: normal; text-align: center; line-height: 1.1; }
}

/* X91 (Grant 2026-10-21: "Items in the past shouldn't be labelled 'Past' they should just have a lower alpha"): ONE fade
   for whatever has ended (a slot, a booking, a band), on both grids. Not opacity on the cell: every cell is opaque, and a
   white veil over it (words included) fades it while the block stays solid, so the row lines never show through a
   booking that spans several rows ("a faint grid line behind a club booking which straddles 2 cells"). Its colour keeps
   its meaning; the slot under way now is not faded. */
.bwc--past { position: relative; }
.bwc--past::after { content: ''; position: absolute; inset: 0; background: rgba(255, 255, 255, .5); pointer-events: none; }

/* The legend's swatch: the same classes on a small flat rectangle, with a hairline so Free shows on white. */
.bwc--swatch {
  display: inline-flex;
  width: 28px;
  height: 20px;
  min-height: 0;
  padding: 0;
  font-size: 10px;
  vertical-align: middle;
  flex: 0 0 auto;
  box-shadow: inset 0 0 0 1px rgb(var(--v-theme-borderLight));
  &.bwc--mine { box-shadow: inset 0 0 0 1px rgb(var(--v-theme-primary)); }
  &.bwc--match { box-shadow: inset 0 0 0 1px rgb(var(--v-theme-success)); }
  &.bwc--meet { box-shadow: inset 0 0 0 1px rgb(var(--v-theme-warning)); }
  &.bwc--changed { box-shadow: inset 0 0 0 2px rgb(var(--v-theme-warning)); }
  /* F-FIX6 (TEST_FT-COMBO UX 4): the amber tint alone is hard to tell from white and grey at legend size, so the Too
     short swatch has a darker amber edge (the app's legend draws the same). */
  &.bwc--short { box-shadow: inset 0 0 0 1px rgb(var(--v-theme-warning)); }
}

/* A phone: short words only, bigger touch targets come from the grid's row height. */
@media (max-width: 640px) {
  .bwc { font-size: 11px; gap: 3px; padding: 0 4px; }
  .bwc-full { display: none; }
  .bwc-mini { display: inline; }
  .bwc-icon { font-size: 12px; }
}
</style>
